import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { di } from '../../../../../../state/inversify.config';
import { AssessmentCreationViewService } from '../../../assessmentCreationView/assessmentCreation.view.service';
import { ModulesSpecsDomain } from '../modules.domain';
import { Notes } from '../../components/notes/notes.dumb';
import { Instructions } from '../../components/instructions/instructions.dumb';
import { Digits } from './digits.dumb';
import { SkipModule } from '../../components/skipModule/skipModule.smart';
import { AssessmentModules } from '../../modules';
import {
  getSortedRandomDigitsList,
  getUpdatedEnabledSequencesList,
  oneOfLongestLengthIsCorrect,
  roundDownToNearestEvenNumber,
  roundUpToNearestEvenNumber,
} from './digitsBackwards.helper';
import { useCustomTranslation } from '../../../../../../translation/customTranslation';

export function DigitsBackwards({ domainService }: any) {
  const moduleSpecsDomain = di.get<ModulesSpecsDomain>(ModulesSpecsDomain);
  const { t } = useCustomTranslation('assessment');
  const assessmentCreationViewService = di.get<AssessmentCreationViewService>(
    AssessmentCreationViewService,
  );
  const module = assessmentCreationViewService.useModuleSelector();

  const { digitsBackwards } = module;
  const moduleName: string = 'digitsBackwards';

  const { heading, instructions, note, data } =
    moduleSpecsDomain.digitsBackwardsConfig();

  const [sortedRandomDigitsList, noop] = useState(
    digitsBackwards?.sequencesPresented
      ? digitsBackwards?.sequencesPresented
      : getSortedRandomDigitsList(data),
  );

  const [sequencesEnabled, setSequencesEnabled] = useState<string[]>([]);

  const saveModule = () => {
    assessmentCreationViewService.dispatchSetAssessmentModule({
      digitsBackwards: {
        sequencesPresented: sortedRandomDigitsList,
        sequencesRecalled: digitsBackwards?.sequencesRecalled,
        digitsBackwardsClinicalNotes:
          digitsBackwards?.digitsBackwardsClinicalNotes,
        skipModuleFlag: digitsBackwards?.skipModuleFlag,
        skipModuleComments: digitsBackwards?.skipModuleComments,
        NORMATIVE_SCORE: 4,
        MAX_SEQUENCES_PRESENTED: 4,
      },
    });
  };

  const setNotesComments = (key: string, value: string) => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      key,
      value,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setCorrectResponse = (digit: string) => {
    const digitBackwardsCorrectResponse = {
      ...digitsBackwards?.sequencesRecalled,
      [digit]: 'Correct',
    };
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'sequencesRecalled',
      digitBackwardsCorrectResponse,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setIncorrectResponse = (digit: string) => {
    const digitBackwardsIncorrectResponse = {
      ...digitsBackwards?.sequencesRecalled,
      [digit]: 'Incorrect',
    };
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'sequencesRecalled',
      digitBackwardsIncorrectResponse,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const enableSequences = (): void => {
    // enable a minimum of two sets of buttons (the shortest sequence length)
    let numberOfIndicesToEnable: number = 2;

    let sequencesRecalledKeys: string[] = [];
    if (digitsBackwards?.sequencesRecalled) {
      sequencesRecalledKeys = Object.keys(digitsBackwards?.sequencesRecalled);
    }

    if (sequencesRecalledKeys.length >= 2) {
      // even number of sequences
      if (sequencesRecalledKeys.length % 2 === 0) {
        // longest sequence length has two correct answers already
        if (oneOfLongestLengthIsCorrect(digitsBackwards)) {
          const recallValue: number = roundDownToNearestEvenNumber(
            sequencesRecalledKeys.length,
          );
          numberOfIndicesToEnable = recallValue + 2;
        } else {
          numberOfIndicesToEnable = roundUpToNearestEvenNumber(
            sequencesRecalledKeys.length,
          );
        }
      } else {
        numberOfIndicesToEnable = roundUpToNearestEvenNumber(
          sequencesRecalledKeys.length,
        );
      }
    }

    setSequencesEnabled(
      // An array containing the two shortest strings
      getUpdatedEnabledSequencesList(
        sortedRandomDigitsList,
        numberOfIndicesToEnable,
      ),
    );
  };

  // Initialize the list of enabled digit sequences starting with the first set of four buttons, or,
  // enabling the buttons that are already answered, plus the next set if appropriate
  useEffect((): void => {
    if (sortedRandomDigitsList.length < 2) {
      throw new Error('Insufficient number of strings');
    }

    enableSequences();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedRandomDigitsList]);

  useEffect((): void => {
    saveModule();
    domainService.dispatchBackEnabled(true);
    domainService.dispatchNextEnabledAndValidate(
      digitsBackwards,
      AssessmentModules.DigitsBackwards,
    );
    enableSequences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digitsBackwards?.sequencesRecalled, digitsBackwards?.skipModuleFlag]);

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        {t(heading)}
      </Typography>
      <Instructions instructions={instructions} />
      <Digits
        sequencesPresented={digitsBackwards?.sequencesPresented}
        sequencesEnabled={sequencesEnabled}
        sequencesRecalled={digitsBackwards?.sequencesRecalled}
        setCorrectResponse={setCorrectResponse}
        setIncorrectResponse={setIncorrectResponse}
      />
      <Notes
        label={t(note.label)}
        keyValue={note.key}
        onSave={setNotesComments}
        data={digitsBackwards?.digitsBackwardsClinicalNotes || ''}
        fullWidth
      />
      <SkipModule
        moduleKeyValue={moduleName}
        skipModuleFlag={digitsBackwards?.skipModuleFlag}
        skipModuleComments={digitsBackwards?.skipModuleComments}
      />
    </>
  );
}
